import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterOutlet } from "@angular/router";
import { delay, filter, map, take } from "rxjs/operators";

import { DomSanitizer } from "@angular/platform-browser";
import { select, Store } from "@ngrx/store";
import { AppState } from "./store";
import { platformState, selectTenantInfo, selectUserPermissions } from "./store/selectors";
import { AuthTokenService } from "./services/auth-token.service";
import { LoadingService } from "./shared/services/loading.service";
import { CookieService } from "ngx-cookie-service";
import { TenantService } from "./services/tenant.service";
import { DEFAULT_FONT_NAME } from "./common/ui-constants";
import { ResourcePreloaderService } from "./services/resource-preloader.service";
import { TopMenuComponent } from "./top-menu/top-menu.component";
import { SourceTrackingComponent } from "./source-tracking/source.tracking.component";
import { RewardfulIntegrationComponent } from "./rewardful-integration/rewardful-integration.component";
import { InstructorChatComponent } from "./instructor-chat/instructor-chat.component";
import { PaypalIntegrationComponent } from "./paypal-integration/paypal-integration.component";
import { FacebookIntegrationComponent } from "./facebook-integration/facebook-integration.component";
import { GoogleAnalyticsIntegrationComponent } from "./google-analytics-integration/google-analytics-integration.component";
import { ThirdPartyScriptsInjectorComponent } from "./third-party-scripts-injector/third-party-scripts-injector.component";
import { MessagesComponent } from "./shared/messages/messages.component";
import { LoadingComponent } from "./shared/loading/loading.component";
import { AiGlobalChatComponent } from "./ai-global-chat/ai-global-chat.component";
import { MixPanelIntegrationComponent } from "./mix-panel-integration/mix-panel-integration.component";
import { AffiliateCookieTrackingComponent } from "./affiliate-cookie-tracking/affiliate-cookie-tracking.component";
import { environment } from "../environments/environment";
import { AiGlobalChatService } from './ai-global-chat/ai-global-chat.service';
import { SocialMediaWidgetComponent } from './shared/social-media-widget/social-media-widget.component';
import {
  StudentEnrolmentDialogContainerComponent
} from "./admin/school-settings/student-enrollment-settings/student-enrolment-dialog-container/student-enrolment-dialog-container.component";
import { OnboardingAnalyticsService } from './services/onboarding-analytics.service';
import { SubscriptionLabelsService } from './services/subscription-labels.service';
import { AppSumoBannerComponent } from './shared/app-sumo-banner/app-sumo-banner.component';
import { LiveSessionBarComponent } from './live-session-bar/live-session-bar.component';
import { BlackFridayBannerComponent } from './shared/black-friday-banner/black-friday-banner.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MetaTagsGeneratorService } from './shared/services/meta-tags-generator.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    TopMenuComponent,
    SourceTrackingComponent,
    RewardfulIntegrationComponent,
    InstructorChatComponent,
    PaypalIntegrationComponent,
    FacebookIntegrationComponent,
    GoogleAnalyticsIntegrationComponent,
    ThirdPartyScriptsInjectorComponent,
    MessagesComponent,
    LoadingComponent,
    AiGlobalChatComponent,
    MixPanelIntegrationComponent,
    AffiliateCookieTrackingComponent,
    SocialMediaWidgetComponent,
    AppSumoBannerComponent,
    LiveSessionBarComponent,
    BlackFridayBannerComponent,
    StudentEnrolmentDialogContainerComponent,
  ],
})
export class AppComponent {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private sanitizer = inject(DomSanitizer);
  private store = inject<Store<AppState>>(Store);
  private loading = inject(LoadingService);
  private tenant = inject(TenantService);
  private metaTags = inject(MetaTagsGeneratorService);

  // Injected to ensure early initialization
  private readonly _token = inject(AuthTokenService);
  private readonly _cookies = inject(CookieService);
  private readonly _preloader = inject(ResourcePreloaderService);

  readonly analyticsService = inject(OnboardingAnalyticsService);

  /**
   * Injected purely so it starts running. It rewords the course-page subscribe button to
   * whatever the school configured, and it has to be alive for the whole app rather than
   * only while the subscription screen is open - the button it renames lives on course
   * pages, sales pages and embeds.
   */
  private readonly subscriptionLabels = inject(SubscriptionLabelsService);
  private readonly aiChatService = inject(AiGlobalChatService);

  // Set once at boot, never reactive
  readonly environment = environment;
  readonly isPlatformLogin = document.location.href.includes("platform-login");

  // Store → signals
  readonly settings = toSignal(
    this.store.pipe(
      select(platformState),
      filter(s => !!s.brandTheme?.primaryColor),
      delay(0)
    )
  );
  private readonly tenantSig = toSignal(this.store.pipe(select(selectTenantInfo)));
  private readonly permissionsSig = toSignal(this.store.pipe(select(selectUserPermissions)));
  readonly isAiChatVisible = toSignal(this.aiChatService.showGlobalAiChat$);

  // Router path as a signal so computed() below retrigger on navigation
  private readonly routePath = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => window.location.pathname)
    ),
    { initialValue: window.location.pathname }
  );

  // Derived state
  readonly isAdminRoute = computed(() => this.routePath().split('/').includes('admin'));
  readonly isLoginPage = computed(() => this.routePath() === '/login-page');

  private readonly showSocialMediaBar = computed(() =>
    this.tenantSig()?.socialMediaBarConfig?.socialMediaBarEnabled ?? true
  );

  readonly isStudentEnrollmentSettingsPresent = computed(() =>
    this.tenant?.tenant?.studentEnrollmentSettings?.enabled ?? false
  );

  readonly loadSocialMediaBar = computed(() => {
    const parts = this.routePath().split('/').filter(Boolean);
    const first = parts[0];
    const allowedPaths = ['courses', 'pages', 'home'];

    const isCourseLessonPath =
      parts.length === 5 && parts[0] === 'courses' && parts[3] === 'lessons';

    const isAllowedPath =
      allowedPaths.includes(first) && (first !== 'pages' || parts.length > 1);

    return (
      this.showSocialMediaBar() &&
      !this.isAdminRoute() &&
      !this.isLoginPage() &&
      !this.isPlatformLogin &&
      !isCourseLessonPath &&
      isAllowedPath
    );
  });

  readonly isNotEmbed = computed(() => {
    const path = this.routePath();
    return (
      !path.includes('certificate-embed') &&
      !path.includes('course-embed') &&
      !path.includes('certificate-proof')
    );
  });

  readonly brandStylesHtml = computed(() => {
    const branding = this.settings();
    if (!branding) return null;

    const primaryColor = branding.brandTheme.primaryColor;
    const accentColor = branding.brandTheme.accentColor;
    const sliderColor = `rgba(${parseInt(primaryColor.substring(1, 3), 16)},${parseInt(primaryColor.substring(3, 5), 16)},${parseInt(primaryColor.substring(5, 7), 16)},0.5)`;

    const brandTheme = this.tenant?.tenant?.brandTheme;
    const websiteFontName = brandTheme?.customFont?.name ?? DEFAULT_FONT_NAME;
    const customCss = brandTheme?.customCss;

    let customStyles = `
    <link href="https://fonts.googleapis.com/icon?family=Material+Icons&cache-busting=599510" rel="stylesheet">
    `;

    if (brandTheme?.customFont?.url) {
      customStyles += `<link href="${brandTheme?.customFont?.url}" rel="stylesheet">`;
    }

    customStyles += `
      <style>

        /* global font */
        h1, h2, h3, h3, h5, h6, span:not(.material-symbols-outlined):not(.material-icons) , div, ul, li, p {
          font-family: ${websiteFontName}, sans-serif !important;
        }

        /* top menu */
        .theme .navbar {
            background: ${primaryColor};
        }
        .theme .menu-item-link.active::after {
          background: ${accentColor};
        }

        .theme .mat-mdc-menu-item .mat-icon-no-color, .mat-mdc-menu-submenu-icon {
            color: #0000008a;
         }

        /* custom-buttons  */
        .theme .btn-primary{
          background: ${primaryColor};
        }
        .theme .btn-accent{
          background: ${accentColor};
        }
        .theme .btn-admin-accent-active{
          background: ${accentColor};
          border:1px solid ${accentColor};
        }
        .theme .btn-admin-primary-active{
          background: ${primaryColor};
          border:1px solid ${primaryColor};
        }
        /* custom-buttons-end  */

        /* buttons  */
        .theme .mat-mdc-raised-button.mat-primary:not([disabled]),
        .theme .mat-mdc-fab.mat-primary:not([disabled]),
        .theme .mat-mdc-mini-fab.mat-primary:not([disabled]) {
            background-color: ${primaryColor};
        }

        .theme .mat-mdc-raised-button.mat-primary,
        .theme .mat-mdc-fab.mat-primary,
        .theme .mat-mdc-mini-fab.mat-primary,
        .theme .mat-mdc-raised-button.mat-accent,
        .theme .mat-mdc-fab.mat-accent,
        .theme .mat-mdc-mini-fab.mat-accent {
            --mat-button-protected-label-text-color: white;
        }

        .theme .mat-button.mat-primary, .theme .mat-icon-button.mat-primary, .theme .mat-stroked-button.mat-primary {
            color: ${primaryColor};
        }

        /* slide toggle */
        .theme .mat-mdc-slide-toggle.mat-primary {
            --mat-slide-toggle-selected-focus-state-layer-color: ${primaryColor};
            --mat-slide-toggle-selected-handle-color: ${primaryColor};
            --mat-slide-toggle-selected-hover-state-layer-color: ${primaryColor};
            --mat-slide-toggle-selected-pressed-state-layer-color: ${primaryColor};
            --mat-slide-toggle-selected-focus-handle-color: ${primaryColor};
            --mat-slide-toggle-selected-hover-handle-color: ${primaryColor};
            --mat-slide-toggle-selected-pressed-handle-color: ${sliderColor};
            --mat-slide-toggle-selected-focus-track-color: ${sliderColor};
            --mat-slide-toggle-selected-hover-track-color: ${sliderColor};
            --mat-slide-toggle-selected-pressed-track-color: ${sliderColor};
            --mat-slide-toggle-selected-track-color: ${sliderColor};
        }

        /* progress spinner */
        .theme .mat-mdc-progress-spinner circle, .mat-mdc-spinner circle {
            stroke: ${primaryColor};
        }

        /* progress bar */
        .theme .mat-mdc-progress-bar {
            --mat-progress-bar-active-indicator-color: ${primaryColor};
        }

        /* regular input fields */
        .theme .mat-mdc-text-field-wrapper.mdc-text-field.mdc-text-field--filled {
            max-height: 50px;
        }

        .theme .mdc-text-field--filled:not(.mdc-text-field--disabled) {
            color: rgba(0, 0, 0, 0.87);
            background-color: #fafafa;
         }

         .theme .mat-mdc-input-element {
            font-weight: 300;
         }

         .theme .mdc-text-field--no-label:not(.mdc-text-field--outlined):not(.mdc-text-field--textarea) .mat-mdc-form-field-infix {
            padding-top: 10px;
            padding-bottom: 10px;
         }

        /* outline input fields */

        .theme .mat-mdc-form-field-infix {
            min-height: 40px;
        }

        .theme .mat-mdc-text-field-wrapper.mdc-text-field--outlined .mat-mdc-form-field-infix {
            padding-top: 8px;
            padding-bottom: 8px;
        }

        .theme .mat-mdc-text-field-wrapper.mdc-text-field.mdc-text-field--outlined {
          font-size: 14px;
        }

        .theme .mat-mdc-text-field-wrapper .mat-mdc-form-field-flex .mat-mdc-floating-label {
            top: 20px;
        }

        .theme .mat-mdc-form-field-hint {
            font-size: 10px;
        }

        /* tabs */
        .theme .mat-mdc-tab:not(.mat-mdc-tab-disabled).mdc-tab--active .mdc-tab__text-label {
            color: black;
            font-weight: 700;
        }

        /* cards */
        .theme .mat-mdc-card {
            color: #000000de;
        }

        .theme .mat-mdc-card-content {
            font-size: 14px;
        }

        .theme .mat-mdc-card-header .mat-mdc-card-header-text .mat-mdc-card-title {
            font-size: 20px;
            font-weight: 500;
            text-align: center;
            line-height: 25px;
            margin-bottom: 10px;
        }

        .theme .mat-mdc-dialog-container {
            background-color: #fafafa;
        }

        ${customCss ? customCss : ""}

      </style>`;

    return this.sanitizer.bypassSecurityTrustHtml(customStyles);
  });

  constructor() {
    console.log("Bootstrapping frontend application  ......");
    this.loading.showLoading();

    // Clear JWT from the URL after other components have had a chance to read it
    this.route.queryParamMap.pipe(
      filter(params => !!params.get("authJwtToken")),
      takeUntilDestroyed()
    ).subscribe(params => {
      const authJwtToken = params.get("authJwtToken");
      const accountCreationEvent = !!params.get("accountCreationEvent");
      const platformPlanPurchasedEvent = !!params.get("platformPlanPurchasedEvent");

      if (authJwtToken && !accountCreationEvent && !platformPlanPurchasedEvent) {
        setTimeout(() => window.history.replaceState(null, null, window.location.pathname), 2000);
      }
    });

    // The home page tags the server puts in every index.html must not stay on other pages
    this.router.events.pipe(
      filter((e): e is NavigationStart => e instanceof NavigationStart),
      take(1)
    ).subscribe(e => this.metaTags.removeServerTagsOutsideHomePage(e.url));

    // One canonical link per page, on the custom domain once it's fully configured
    effect(() => {
      this.routePath();
      this.tenantSig();
      this.metaTags.updateCanonical();
    });

    // Remove the app-shell skeleton loader after the first navigation completes
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      take(1)
    ).subscribe(() => (window as any).__ochRemoveAppShellLoader?.());
  }

  isPlatformAdmin() {
    return this.permissionsSig()?.isPlatformAdmin;
  }

  isAdmin() {
    return this.permissionsSig()?.isAdmin;
  }
}
